import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { batch, useDispatch, useSelector } from 'react-redux'
import { useTheme } from 'styled-components'
import { useEffectOnce, useSecondOrderCallback, useSwitch, useViewport } from '@chilecompra/react-kit'
import { Card, Popover, PopoverMenuItem, Typography } from '@chilecompra/react-kit/components'
import DrillDownColumnsChart from '../../components/Charts/DrillDownColumnsChart'
import ViewOption from '../../components/Custom/ViewOption'
import { formatAmount, minFormatter } from '../../modules/utils/formatters'
import { DOWNLOADS_MONTH_OPTIONS } from '../../config/settings/constants'
import {
  onAccumulatedAmountDetailThunk,
  onAccumulatedAmountThunk,
  setCumulativeAmountYearSuccess
} from './HomeTradedAmounts.actions'
import { Anchor, AnchorIcon, Content, GridContainer, GridItem, Root, WrapperText } from './HomeTradedAmounts.styles'

/**
 * The HomeTradedAmounts's container.
 */
const HomeTradedAmounts = () => {
  const dispatch = useDispatch()
  const { accumulatedAmount, accumulatedAmountDetail, cumulativeAmountYear } = useSelector(
    state => state.homeTradedAmounts
  )
  const theme = useTheme()
  const [yearOpen, setYearOpen] = useState(false)
  const [viewOptions, setViewOptions] = useState([
    { disabled: true, type: 'currency' },
    { disabled: false, type: 'toggleChart' }
  ])
  const { size } = useViewport()
  const { value: checked, onChange: handleSwitch } = useSwitch()
  const handleCloseYear = useCallback(() => setYearOpen(false), [])
  const handleOpenYear = useCallback(() => setYearOpen(true), [])
  const createHandleAccumulatedAmountChange = useSecondOrderCallback(
    (_, updatedValue) => {
      dispatch(setCumulativeAmountYearSuccess(accumulatedAmount?.find(datum => datum.year === updatedValue)))
    },
    [accumulatedAmount]
  )
  const enhancedAccumulatedAmountData = useMemo(
    () => ({
      data: accumulatedAmount?.map(datum => ({
        ...datum,
        color: theme.palette.brand.primary,
        drilldown: datum.year,
        name: datum.year,
        y: checked ? datum?.amountUSD : datum?.amountCLP
      })),
      name: 'Monto acumulado'
    }),
    [accumulatedAmount, checked]
  )
  const enhancedAccumulatedAmountSubData = useMemo(
    () =>
      accumulatedAmountDetail?.map(datum => ({
        id: datum.year,
        name: datum.year,
        data: datum?.data?.map(item => [item?.month, checked ? item?.amountUSD : item?.amountCLP, item.qtyOC])
      })),
    [accumulatedAmountDetail, checked]
  )
  const handleDataLabelFormatterAccumulatedAmount = useCallback(function () {
    return `<strong color="${
      theme.palette.brand.black3
    }">${checked ? '$USD' : '$'} ${minFormatter(this.y) || 0}</strong>`
  }, [])

  const handleTooltipFormatterAccumulatedAmount = useCallback(
    function () {
      return `Monto total transado el ${monthValidation(this.key) ? 'mes' : 'año'} <strong>${
        this?.point?.name
      }</strong>: <strong>${checked ? '$USD' : '$'} ${formatAmount(
        this.point.y
      )}.</strong><br>Órdenes de compra: <strong>${
        monthValidation(this.key)
          ? formatAmount(this?.point?.series?.options?.data?.find(month => month[0] === this?.key)[2])
          : formatAmount(this?.point?.qtyOC)
      }.</strong>`
    },
    [checked]
  )
  const viewType = viewOptions?.find(option => option.disabled)?.type
  const monthValidation = monthName => DOWNLOADS_MONTH_OPTIONS.some(month => month.name === monthName)
  useEffect(() => {
    batch(() => {
      accumulatedAmount.forEach(datum => dispatch(onAccumulatedAmountDetailThunk({ year: datum.year })))
      dispatch(setCumulativeAmountYearSuccess(accumulatedAmount[accumulatedAmount.length - 1]))
    })
  }, [accumulatedAmount])
  useEffectOnce(() => {
    dispatch(onAccumulatedAmountThunk())
  })

  return (
    <Root>
      <Content>
        <Typography
          color="primary"
          fontSize="18px"
          fontWeight="bold"
          lineHeight="21px"
          margin={size.isUpToMedium ? '96px 0 0' : '48px 0 24px'}
          variant="body1"
        >
          Gastos realizados a través de Mercado Público
        </Typography>
        <Typography
          fontSize="34px"
          fontWeight="bold"
          lineHeight="38px"
          margin={size.isUpToMedium ? '12px 0' : '0 0 24px'}
          variant="h2"
          width={size.isUpToMedium ? '485px' : 'auto'}
        >
          Monto total transado por el Estado de Chile
        </Typography>
        <Typography fontSize="18px" lineHeight="24px" width={size.isUpToMedium ? '448px' : 'auto'} variant="body1">
          Corresponde a la cifra acumulada de todas las transacciones que se realizaron través de Mercado Público.
        </Typography>
        <GridContainer justifyContent="center">
          <GridItem xs={size.isUpToMedium ? 10 : 12}>
            <ViewOption
              checked={checked}
              onChange={handleSwitch}
              viewOptions={viewOptions}
              setViewOptions={setViewOptions}
            />
            <Card color="blank" elevation="1" margin="32px 0 0">
              {viewType === 'currency' && (
                <GridContainer>
                  <GridItem md={6}>
                    <Typography
                      display="inline"
                      fontSize="16px"
                      lineHeight="24px"
                      margin={size.isUpToMedium ? '0 4px 0 0' : '24px 4px 0 0'}
                      variant="body1"
                    >
                      Monto transado durante el año:
                    </Typography>
                    <Popover
                      anchor={
                        <Anchor>
                          <Typography
                            display="inline"
                            fontSize="16px"
                            fontWeight="bold"
                            lineHeight="24px"
                            variant="body1"
                          >
                            {cumulativeAmountYear?.year}
                          </Typography>
                          <AnchorIcon open={yearOpen} />
                        </Anchor>
                      }
                      open={yearOpen}
                      onClose={handleCloseYear}
                      onOpen={handleOpenYear}
                    >
                      {accumulatedAmount.map(option => (
                        <PopoverMenuItem
                          enableClose
                          key={option.year}
                          onClick={createHandleAccumulatedAmountChange(option.year)}
                        >
                          {option.year}
                        </PopoverMenuItem>
                      ))}
                    </Popover>
                    <Typography
                      color="primary"
                      fontSize={size.isUpToMedium ? '38px' : '28px'}
                      fontWeight="bold"
                      lineHeight="38px"
                      margin={size.isUpToMedium ? '12px 0 0' : '24px 0'}
                      variant="h3"
                    >
                      {checked ? '$USD' : '$'}{' '}
                      {formatAmount(checked ? cumulativeAmountYear?.amountUSD : cumulativeAmountYear?.amountCLP)}
                    </Typography>
                  </GridItem>
                  <GridItem md={6}>
                    <WrapperText padding={size.isUpToMedium ? '0 16px' : undefined}>
                      <Typography fontSize="16px" lineHeight="24px" variant="body1">
                        Durante el <strong>año {cumulativeAmountYear?.year}</strong> un total de{' '}
                        <strong>{cumulativeAmountYear?.qtyOrganismAnnual} organismos compradores</strong> realizaron
                        compras a través de Mercado Público, generando un total de{' '}
                        <strong>{formatAmount(cumulativeAmountYear?.qtyOC)}</strong> órdenes de compras.
                      </Typography>
                    </WrapperText>
                  </GridItem>
                </GridContainer>
              )}
              {viewType === 'toggleChart' && (
                <GridContainer>
                  <DrillDownColumnsChart
                    data={[enhancedAccumulatedAmountData]}
                    dataLabelFormatter={handleDataLabelFormatterAccumulatedAmount}
                    id="accumulated-amount-columns-chart"
                    subData={enhancedAccumulatedAmountSubData}
                    tooltipFormatter={handleTooltipFormatterAccumulatedAmount}
                  />
                </GridContainer>
              )}
            </Card>
            <Card color="primary" margin={size.isUpToMedium ? '32px 0 96px' : '48px 0'}>
              <Typography fontSize="16px" lineHeight="20px" variant="body1">
                Conforme a lo señalado en el artículo 3 letra e) de la Ley N°19.886 de Compras Públicas y en el Decreto
                N°75 de 2004 del Ministerio de Obras Públicas que aprueba el Reglamento para los contratos de obra
                pública, el Ministerio de Obras Públicas y el Ministerio de Vivienda y Urbanismo no están obligados a
                emitir las órdenes de compra relacionadas a contratos de ejecución y concesión de obra pública a través
                de la plataforma Mercado Público.
              </Typography>
            </Card>
          </GridItem>
        </GridContainer>
      </Content>
    </Root>
  )
}

export default HomeTradedAmounts
